import { useEffect } from 'react'
import { isSecretLinkPage } from '@/utils/secretLinkPage'

const META_PIXEL_ID = '813144668252409'

type FbqFunction = {
    (...args: unknown[]): void
    callMethod?: (...args: unknown[]) => void
    queue: unknown[][]
    push: (...args: unknown[]) => void
    loaded: boolean
    version: string
}

declare global {
    interface Window {
        fbq?: FbqFunction
        _fbq?: FbqFunction
    }
}

/**
 * Hook to initialize Meta Pixel (Facebook Pixel) on a page.
 * Safe to call multiple times - will only initialize once.
 * Injects the pixel script and noscript fallback.
 */
export function useMetaPixel(): void {
    useEffect(() => {
        // The pixel reports the page it loads on; a signing link's address is the signer's credential.
        if (isSecretLinkPage()) {
            return
        }

        // Check if Meta Pixel is already initialized
        if (window.fbq) {
            return
        }

        // Initialize fbq function (exactly as Meta's snippet)
        const n = function (...args: unknown[]) {
            if (n.callMethod) {
                n.callMethod(...args)
            } else {
                n.queue.push(args)
            }
        } as FbqFunction

        n.push = n
        n.loaded = true
        n.version = '2.0'
        n.queue = []

        window.fbq = n
        if (!window._fbq) {
            window._fbq = n
        }

        // Create and inject Meta Pixel script into head
        const script = document.createElement('script')
        script.async = true
        script.src = 'https://connect.facebook.net/en_US/fbevents.js'
        const firstScript = document.getElementsByTagName('script')[0]
        firstScript?.parentNode?.insertBefore(script, firstScript)

        // Initialize pixel and track page view (once — the `if (window.fbq)` guard above
        // makes this idempotent across re-mounts).
        window.fbq('init', META_PIXEL_ID)
        window.fbq('track', 'PageView')

        // NOTE: deliberately NO <noscript> fallback img here. The JS `fbq('track','PageView')`
        // above already fired the browser pixel; injecting the noscript tracking image from
        // JS makes it fire too (the browser loads <noscript> content when scripting is on for
        // the injected node), producing a DUPLICATE PageView. A noscript fallback only makes
        // sense as static markup in index.html for genuinely JS-disabled clients.
    }, [])
}
